import { IEquipment } from '@frontend/equipment/types';
import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { PackageType } from '@frontend/package/types';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { StockClient } from '@frontend/stock/api';
import { Stock } from '@frontend/stock/types';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import { ScreenTrigger, Step } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useIdentification, useScanners } from '../../../hooks';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { authenticateKioskUser } from '../../utils/authenticate-kiosk-user';
import { useScreen } from '../useScreen';

export interface LendingReturnAvailableDetailViewProps {
    equipment: IEquipment | null;
    product: IProduct | null;
    stock: Stock | null;
    slot: Slot | null;
    isLoading: boolean;
    isProcessing: boolean;
    isAuthenticating: boolean;
    isIdentifying: boolean;
    error: boolean;
    authenticationError: boolean;
    requiresAuthentication: boolean;
    submitDisabled: boolean;
    resetAuthenticationError: () => void;
    onBack: () => void;
    onSubmit: () => void;
}

export const slotContainsOtherStock = (equipmentId: string, stocks: Stock[]): boolean =>
    stocks.some((stock) => stock.quantity > 0 && stock.equipment_id !== equipmentId);

const SENSOR_TIMEOUT = 10000;

export const useLendingReturnAvailableDetail = (): LendingReturnAvailableDetailViewProps => {
    const location = useLocation();
    const { navigationState, dispatch, store, wizardId, screen_type, wizardData, updateWizardData, onBack, onSubmit } = useScreen();
    const transactionWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value as
        | string
        | undefined;
    const packageWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.AUDIT_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
    const locationEquipment = location.state?.equipment as IEquipment | undefined;
    const equipment = (wizardData.equipment ?? wizardData.selectedUserEquipment ?? locationEquipment ?? null) as IEquipment | null;

    const [product, setProduct] = useState<IProduct | null>((wizardData.product as IProduct | undefined) ?? null);
    const [stock, setStock] = useState<Stock | null>(null);
    const [slot, setSlot] = useState<Slot | null>(null);
    const [hasOtherStock, setHasOtherStock] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isIdentifying, setIsIdentifying] = useState(false);
    const [error, setError] = useState(false);
    const [authenticationError, setAuthenticationError] = useState(false);
    const updateWizardDataRef = useRef(updateWizardData);
    const productRef = useRef(product);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { syncTransaction } = useWizardTransaction({
        wizardId,
        store,
        autoFetch: false
    });
    const { localUpdatePackage } = useWizardPackage({
        wizardId,
        store,
        autoFetch: false,
        autoCreate: false
    });

    useEffect(() => {
        updateWizardDataRef.current = updateWizardData;
    }, [updateWizardData]);

    useEffect(() => {
        productRef.current = product;
    }, [product]);

    const submitStep = useMemo(
        () => store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT),
        [screen_type, store?.steps]
    );

    useEffect(() => {
        if (!equipment) {
            setError(true);
            setIsLoading(false);
            return;
        }

        const accountId = navigationState.iot?.account_id ?? equipment.account_id;
        const spotId = navigationState.iot?.spot_id ?? navigationState.spot?.id;
        let cancelled = false;

        const resolveAvailability = async () => {
            setIsLoading(true);
            setError(false);

            try {
                const resolvedProduct =
                    productRef.current?.id === equipment.product_id
                        ? productRef.current
                        : await ProductClient.fetchProduct(equipment.account_id, equipment.product_id);
                if (cancelled) return;
                productRef.current = resolvedProduct;
                setProduct(resolvedProduct);

                if (!spotId) {
                    throw new Error('Missing current spot while resolving available lending equipment');
                }

                const equipmentStocks = await StockClient.fetchStocks({
                    account_id: accountId,
                    spot_id: spotId,
                    equipment_id: equipment.id
                });
                if (cancelled) return;

                const equipmentStock = equipmentStocks.results.find((item) => item.quantity > 0) ?? null;
                if (!equipmentStock) {
                    setStock(null);
                    setSlot(null);
                    setHasOtherStock(false);
                    updateWizardDataRef.current({
                        equipment,
                        product: resolvedProduct,
                        stock: null,
                        slot: null,
                        requiresAuthenticationForSlot: false
                    });
                    return;
                }

                const [resolvedSlot, slotStocks] = await Promise.all([
                    SlotClient.fetchSlot(accountId, equipmentStock.spot_id, equipmentStock.module_id, equipmentStock.slot_id),
                    StockClient.fetchStocks({
                        account_id: accountId,
                        spot_id: equipmentStock.spot_id,
                        module_id: equipmentStock.module_id,
                        slot_id: equipmentStock.slot_id,
                        size: '1000'
                    })
                ]);
                if (cancelled) return;

                const containsOtherStock = slotContainsOtherStock(equipment.id, slotStocks.results);
                setStock(equipmentStock);
                setSlot(resolvedSlot);
                setHasOtherStock(containsOtherStock);
                updateWizardDataRef.current({
                    equipment,
                    product: resolvedProduct,
                    stock: equipmentStock,
                    slot: resolvedSlot,
                    requiresAuthenticationForSlot: containsOtherStock
                });
            } catch (cause) {
                Logger.error('Failed to resolve available lending equipment stock', cause);
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        resolveAvailability();

        return () => {
            cancelled = true;
        };
    }, [equipment, navigationState.iot?.account_id, navigationState.iot?.spot_id, navigationState.spot?.id]);

    const requiresAuthentication = hasOtherStock && !navigationState.user;
    const nextStep: Step | undefined = submitStep;

    const clearSensorTimeout = useCallback(() => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    }, []);

    const handleAuthenticationFailed = useCallback(() => {
        clearSensorTimeout();
        setIsIdentifying(false);
        setIsAuthenticating(false);
        setAuthenticationError(true);
    }, [clearSensorTimeout]);

    const authenticateUser = useCallback(
        async (user: IUser) => {
            if (!requiresAuthentication) return;
            if (!navigationState.iot) {
                handleAuthenticationFailed();
                return;
            }

            clearSensorTimeout();
            setIsIdentifying(false);
            setIsAuthenticating(true);
            setAuthenticationError(false);

            const success = await authenticateKioskUser(dispatch, navigationState.iot.account_id, navigationState.iot.id, user);
            if (!success) {
                handleAuthenticationFailed();
                return;
            }

            setIsAuthenticating(false);
        },
        [clearSensorTimeout, dispatch, handleAuthenticationFailed, navigationState.iot, requiresAuthentication]
    );

    useScanners({
        id: 'LENDING_RETURN_AVAILABLE_DETAIL',
        onDeviceSensorReceived: () => {
            if (!requiresAuthentication || isAuthenticating) return;
            setIsIdentifying(true);
            setAuthenticationError(false);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                handleAuthenticationFailed();
            }, SENSOR_TIMEOUT);
        },
        onTagScanned: () => {
            if (!requiresAuthentication || isAuthenticating) return;
            setIsIdentifying(true);
            setAuthenticationError(false);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                handleAuthenticationFailed();
            }, SENSOR_TIMEOUT);
        }
    });

    useIdentification({
        id: 'LENDING_RETURN_AVAILABLE_DETAIL',
        onNoMatchingIdentificationFound: () => {
            if (!requiresAuthentication) return;
            handleAuthenticationFailed();
        },
        onUnlinkedIdentificationFound: () => {
            if (!requiresAuthentication) return;
            handleAuthenticationFailed();
        },
        onUserIdentified: (user) => {
            if (!requiresAuthentication) return;
            void authenticateUser(user);
        }
    });

    useEffect(() => {
        return () => {
            clearSensorTimeout();
        };
    }, [clearSensorTimeout]);

    const handleSubmit = async () => {
        if (!nextStep || isProcessing) return;

        if (requiresAuthentication) return;

        if (!stock || !slot) {
            onSubmit(nextStep);
            return;
        }

        if (!store?.functionality || !equipment || !product || !transactionWorkflowId || !packageWorkflowId) {
            setError(true);
            return;
        }

        setIsProcessing(true);
        setError(false);
        try {
            const transaction = await syncTransaction({
                application: store.functionality.application,
                feature: store.functionality.feature,
                state: TransactionStateName.TRANSACTION_CREATED,
                workflow_id: transactionWorkflowId,
                type: TransactionType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                user_id: navigationState.user?.id,
                iot_id: navigationState.iot?.id
            });

            const auditPackage = await PackageClient.postPackage(transaction.account_id, transaction.id, {
                application: store.functionality.application,
                feature: store.functionality.feature,
                workflow_id: packageWorkflowId,
                user_id: transaction.user_id,
                quantity: 1,
                type: PackageType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                product_id: product.id,
                equipment_id: equipment.id,
                priority: 1,
                blocking: false
            });
            await localUpdatePackage(auditPackage);
            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS
            });
            onSubmit(nextStep);
        } catch (cause) {
            Logger.error('Failed to create audit transaction for available lending equipment', cause);
            setError(true);
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        equipment,
        product,
        stock,
        slot,
        isLoading,
        isProcessing,
        isAuthenticating,
        isIdentifying,
        error,
        authenticationError,
        requiresAuthentication,
        submitDisabled:
            isLoading ||
            isProcessing ||
            isAuthenticating ||
            isIdentifying ||
            requiresAuthentication ||
            error ||
            !nextStep ||
            (!!stock && !!slot && !requiresAuthentication && (!store?.functionality || !transactionWorkflowId || !packageWorkflowId)),
        resetAuthenticationError: () => setAuthenticationError(false),
        onBack,
        onSubmit: handleSubmit
    };
};
